import { IFilter } from "@saleor/components/Filter";
import { checkUserType } from "@saleor/sellerOrders/util";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import {
  createAutocompleteField,
  createDateField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum SellerOrdersFilterKeys {
  mall = "mall",
  orderDate = "orderDate",
  completedAt = "completedAt",
  orderStatus = "orderStatus",
  shippingMethodName = "shippingMethodName"
}

export enum orderStatus {
  newOrder = "new order",
  acknowledged = "acknowledged",
  ready = "ready",
  collected = "collected",
  receivedAtFc = "received at fc",
  packed = "packed",
  readyToShip = "ready to ship",
  shipped = "shipped",
  completed = "completed",
  canceled = "canceled"
}

export enum deliveryType {
  EXPRESS = "express",
  IN_STORE_PICKUP = "in_store_pickup",
  NEXT_DAY = "next_day",
  EXPRESS_LONG_DISTANCE = "express_long_distance"
}

export interface SellerOrdersFilterOpts {
  mall: FilterOpts<string[]> & AutocompleteFilterOpts;
  orderDate: FilterOpts<MinMax>;
  completedAt: FilterOpts<MinMax>;
  orderStatus: FilterOpts<string[]>;
  shippingMethodName: FilterOpts<string[]>;
}

const messages = defineMessages({
  acknowledged: {
    defaultMessage: "ACKNOWLEDGED",
    description: "acknowledged"
  },
  canceled: {
    defaultMessage: "CANCELED",
    description: "canceled status"
  },
  collected: {
    defaultMessage: "COLLECTED",
    description: "collected status"
  },
  completed: {
    defaultMessage: "COMPLETED",
    description: "completed status"
  },
  completedAt: {
    defaultMessage: "Order Completion Date",
    description: "order completion date"
  },
  express: {
    defaultMessage: "Express",
    description: "express"
  },
  expressLongDistance: {
    defaultMessage: "10km Express Delivery",
    description: "expressLongDistance"
  },
  inStorePickup: {
    defaultMessage: "In Store Pickup ",
    description: "in store pickup "
  },
  mall: {
    defaultMessage: "Mall",
    description: "mall name"
  },
  newOrder: {
    defaultMessage: "NEW ORDER",
    description: "new order"
  },
  nextDay: {
    defaultMessage: "Next Day Delivery",
    description: "Next Day Delivery"
  },
  orderDate: {
    defaultMessage: "Order Created Date",
    description: "order created date"
  },
  orderStatus: {
    defaultMessage: "Order Status",
    description: "order status"
  },
  packed: {
    defaultMessage: "PACKED",
    description: "packed status"
  },
  ready: {
    defaultMessage: "READY",
    description: "ready"
  },
  readyToShip: {
    defaultMessage: "READY TO SHIP",
    description: "ready to ship status"
  },
  receivedAtFc: {
    defaultMessage: "RECEIVED AT FC",
    description: "received at fc status"
  },
  shipped: {
    defaultMessage: "SHIPPED",
    description: "shipped status"
  },
  shippingMethodName: {
    defaultMessage: "Delivery Type",
    description: "delivery type"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: SellerOrdersFilterOpts,
  userType: SmopUserTypeCode
): IFilter<SellerOrdersFilterKeys> {
  return [
    {
      ...createDateField(
        SellerOrdersFilterKeys.completedAt,
        intl.formatMessage(messages.completedAt),
        opts.completedAt.value
      ),
      active: opts.completedAt.active
    },
    {
      ...createOptionsField(
        SellerOrdersFilterKeys.orderStatus,
        intl.formatMessage(messages.orderStatus),
        opts.orderStatus.value,
        true,
        [
          {
            label: intl.formatMessage(messages.newOrder),
            value: orderStatus.newOrder
          },
          {
            label: intl.formatMessage(messages.acknowledged),
            value: orderStatus.acknowledged
          },
          {
            label: intl.formatMessage(messages.ready),
            value: orderStatus.ready
          },
          {
            label: intl.formatMessage(messages.collected),
            value: orderStatus.collected
          },
          {
            label: intl.formatMessage(messages.receivedAtFc),
            value: orderStatus.receivedAtFc
          },
          {
            label: intl.formatMessage(messages.packed),
            value: orderStatus.packed
          },
          {
            label: intl.formatMessage(messages.readyToShip),
            value: orderStatus.readyToShip
          },
          {
            label: intl.formatMessage(messages.shipped),
            value: orderStatus.shipped
          },
          {
            label: intl.formatMessage(messages.completed),
            value: orderStatus.completed
          },
          {
            label: intl.formatMessage(messages.canceled),
            value: orderStatus.canceled
          }
        ]
      ),
      active: opts.orderStatus.active
    },
    {
      ...createOptionsField(
        SellerOrdersFilterKeys.shippingMethodName,
        intl.formatMessage(messages.shippingMethodName),
        opts.shippingMethodName.value,
        true,
        [
          {
            label: intl.formatMessage(messages.express),
            value: deliveryType.EXPRESS
          },
          {
            label: intl.formatMessage(messages.nextDay),
            value: deliveryType.NEXT_DAY
          },
          {
            label: intl.formatMessage(messages.inStorePickup),
            value: deliveryType.IN_STORE_PICKUP
          },
          {
            label: intl.formatMessage(messages.expressLongDistance),
            value: deliveryType.EXPRESS_LONG_DISTANCE
          }
        ]
      ),
      active: opts.shippingMethodName.active
    },
    {
      ...createDateField(
        SellerOrdersFilterKeys.orderDate,
        intl.formatMessage(messages.orderDate),
        opts.orderDate.value
      ),
      active: opts.orderDate.active
    },
    {
      ...createAutocompleteField(
        SellerOrdersFilterKeys.mall,
        intl.formatMessage(messages.mall),
        opts.mall.value,
        opts.mall.displayValues,
        true,
        opts.mall.choices,
        {
          hasMore: opts.mall.hasMore,
          initialSearch: "",
          loading: opts.mall.loading,
          onFetchMore: opts.mall.onFetchMore,
          onSearchChange: opts.mall.onSearchChange
        }
      ),
      active: opts.mall.active
    }
  ].splice(0, checkUserType(userType));
}
